/**
 * Password Gate - 3-State Flow
 * Screen 1: Password entry
 * Screen 2: Request code (email + phone)
 * Screen 3: Code revealed
 */

class PasswordGate {
  constructor() {
    this.config = this.loadConfig();
    this.elements = this.getElements();

    if (!this.config || !this.elements.overlay) {
      console.error('Password Gate: Configuration or elements not found');
      return;
    }

    this.init();
  }

  /**
   * Load configuration from script tag
   */
  loadConfig() {
    const configElement = document.querySelector('[data-password-gate-config]');
    if (!configElement) return null;

    try {
      return JSON.parse(configElement.textContent);
    } catch (error) {
      console.error('Password Gate: Failed to parse config', error);
      return null;
    }
  }

  /**
   * Get all DOM elements
   */
  getElements() {
    return {
      overlay: document.querySelector('[data-password-gate-overlay]'),
      modal: document.querySelector('[data-password-gate-modal]'),
      bgImage: document.querySelector('[data-bg-image]'),

      // Password entry state
      passwordState: document.querySelector('[data-password-entry-state]'),
      passwordForm: document.querySelector('[data-password-form]'),
      passwordInput: document.querySelector('[data-password-input]'),
      passwordSubmitBtn: document.querySelector('[data-password-submit]'),
      passwordError: document.querySelector('[data-password-error]'),
      passwordErrorText: document.querySelector('[data-password-error-text]'),
      showRequestBtn: document.querySelector('[data-show-request-form]'),

      // Request code state
      requestState: document.querySelector('[data-request-code-state]'),
      requestForm: document.querySelector('[data-request-form]'),
      emailInput: document.querySelector('[data-contact-email-input]'),
      phoneInput: document.querySelector('[data-contact-phone-input]'),
      requestSubmitBtn: document.querySelector('[data-request-submit]'),
      requestSubmitText: document.querySelector('[data-request-submit-text]'),
      requestSpinner: document.querySelector('[data-request-spinner]'),
      requestError: document.querySelector('[data-request-error]'),
      requestErrorText: document.querySelector('[data-request-error-text]'),
      backBtn: document.querySelector('[data-back-to-password]'),

      // Code revealed state
      codeRevealedState: document.querySelector('[data-code-revealed-state]'),
      codeDisplay: document.querySelector('[data-code-display]'),
      copyBtn: document.querySelector('[data-copy-btn]'),
      continueBtn: document.querySelector('[data-continue-btn]'),

      // Success overlay
      successOverlay: document.querySelector('[data-success-overlay]')
    };
  }

  /**
   * Initialize the password gate
   */
  init() {
    // Check if user already has access
    if (this.checkAccess()) {
      this.hideGate();
      return;
    }

    // Show the gate
    this.showGate();
    this.bindEvents();

    // Focus on password input
    setTimeout(() => {
      this.elements.passwordInput?.focus();
    }, 300);
  }

  /**
   * Check if user has valid access cookie
   */
  checkAccess() {
    const cookie = this.getCookie(this.config.cookieName);
    return cookie === 'authorized';
  }

  /**
   * Bind all event listeners
   */
  bindEvents() {
    // Password form submission (Enter key)
    this.elements.passwordInput?.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.handlePasswordSubmit();
      }
    });

    // Password form submission (Submit button)
    const submitBtn = document.querySelector('[data-password-submit]');
    if (submitBtn) {
      submitBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.handlePasswordSubmit();
      });
    }

    // Show request form
    this.elements.showRequestBtn?.addEventListener('click', () => {
      this.showRequestForm();
    });

    // Back to password
    this.elements.backBtn?.addEventListener('click', () => {
      this.showPasswordForm();
    });

    // Request form submission (keep for Enter key support)
    this.elements.requestForm?.addEventListener('submit', (e) => {
      e.preventDefault();
      this.handleRequestSubmit();
    });

    // Reveal button click
    this.elements.requestSubmitBtn?.addEventListener('click', () => {
      this.handleRequestSubmit();
    });

    // Continue button (from code revealed to password entry)
    this.elements.continueBtn?.addEventListener('click', () => {
      this.showPasswordForm();
    });

    // Code display click to copy
    this.elements.codeDisplay?.addEventListener('click', () => {
      this.handleCopyCode();
    });

    // Prevent closing by clicking overlay
    this.elements.overlay?.addEventListener('click', (e) => {
      if (e.target === this.elements.overlay) {
        // Shake animation to indicate it can't be closed
        this.elements.modal?.classList.add('shake');
        setTimeout(() => {
          this.elements.modal?.classList.remove('shake');
        }, 500);
      }
    });
  }

  /**
   * Handle password form submission
   */
  handlePasswordSubmit() {
    const enteredPassword = this.elements.passwordInput?.value.trim();

    if (!enteredPassword) {
      this.showPasswordError('Please enter a code');
      return;
    }

    if (this.validatePassword(enteredPassword)) {
      this.handleSuccess();
    } else {
      this.showPasswordError(this.config.errorMessage);
      // Shake the input
      this.elements.passwordInput?.classList.add('shake');
      setTimeout(() => {
        this.elements.passwordInput?.classList.remove('shake');
      }, 500);
    }
  }

  /**
   * Validate password (case-insensitive)
   */
  validatePassword(password) {
    return password.toLowerCase() === this.config.password.toLowerCase();
  }

  /**
   * Show password error
   */
  showPasswordError(message) {
    if (this.elements.passwordError && this.elements.passwordErrorText) {
      this.elements.passwordErrorText.textContent = message;
      this.elements.passwordError.style.display = 'flex';
      this.elements.passwordInput?.classList.add('error');
    }
  }

  /**
   * Hide password error
   */
  hidePasswordError() {
    if (this.elements.passwordError) {
      this.elements.passwordError.style.display = 'none';
      this.elements.passwordInput?.classList.remove('error');
    }
  }

  /**
   * Handle successful password entry
   */
  handleSuccess() {
    // Hide password error
    this.hidePasswordError();

    // Show success overlay
    if (this.elements.successOverlay) {
      this.elements.successOverlay.classList.remove('hidden');
    }

    // Set cookie
    this.setCookie(
      this.config.cookieName,
      'authorized',
      this.config.cookieDurationHours
    );

    // Wait for success message display, then hide gate
    setTimeout(() => {
      this.hideGate();
    }, this.config.successDelay || 2500);
  }

  /**
   * Show request code form (State 2)
   */
  showRequestForm() {
    // Swap to image 2
    if (this.elements.bgImage) {
      this.elements.bgImage.src = this.config.bgImage2;
    }

    this.elements.passwordState?.classList.add('hidden');
    this.elements.requestState?.classList.remove('hidden');
    this.elements.codeRevealedState?.classList.add('hidden');
    this.hidePasswordError();
    this.hideRequestError();

    // Clear form
    if (this.elements.emailInput) this.elements.emailInput.value = '';
    if (this.elements.phoneInput) this.elements.phoneInput.value = '';

    // Focus on email input
    setTimeout(() => {
      this.elements.emailInput?.focus();
    }, 300);
  }

  /**
   * Show password form (State 1)
   */
  showPasswordForm() {
    // Swap back to image 1
    if (this.elements.bgImage) {
      this.elements.bgImage.src = this.config.bgImage1;
    }

    this.elements.requestState?.classList.add('hidden');
    this.elements.codeRevealedState?.classList.add('hidden');
    this.elements.passwordState?.classList.remove('hidden');
    this.hidePasswordError();
    this.hideRequestError();

    // Focus on password input
    setTimeout(() => {
      this.elements.passwordInput?.focus();
    }, 300);
  }

  /**
   * Show code revealed (State 3)
   */
  showCodeRevealed() {
    // Keep image 2 for state 3
    if (this.elements.bgImage) {
      this.elements.bgImage.src = this.config.bgImage2;
    }

    this.elements.passwordState?.classList.add('hidden');
    this.elements.requestState?.classList.add('hidden');
    this.elements.codeRevealedState?.classList.remove('hidden');

    // Display the code
    if (this.elements.codeDisplay) {
      this.elements.codeDisplay.textContent = this.config.password.toUpperCase();
    }

    // Focus on continue button
    setTimeout(() => {
      this.elements.continueBtn?.focus();
    }, 300);
  }

  /**
   * Handle request form submission
   */
  async handleRequestSubmit() {
    const email = this.elements.emailInput?.value.trim();
    const phone = this.elements.phoneInput?.value.trim();

    // Validate both fields are filled
    if (!email || !phone) {
      this.showRequestError('Please provide both email and phone number');
      return;
    }

    // Show loading state
    this.setRequestLoading(true);
    this.hideRequestError();

    try {
      await this.submitToKlaviyo(email, phone);
      // Show code revealed screen
      this.showCodeRevealed();
    } catch (error) {
      console.error('Klaviyo submission error:', error);
      this.showRequestError(this.config.requestErrorMessage);
    } finally {
      this.setRequestLoading(false);
    }
  }

  /**
   * Submit contact info to Klaviyo using client-side API
   */
  async submitToKlaviyo(email, phone) {
    console.log('=== Klaviyo Submission Started ===');
    console.log('Email:', email);
    console.log('Phone:', phone);

    // Check if Klaviyo is loaded
    if (typeof window._learnq === 'undefined') {
      console.warn('⚠️ Klaviyo _learnq not found, initializing...');
      window._learnq = window._learnq || [];
    } else {
      console.log('✓ Klaviyo _learnq exists');
    }

    try {
      // Use Klaviyo's identify method
      const identifyData = {
        '$email': email,
        '$phone_number': phone,
        'requested_code': true,
        'campaign': 'skinbetter_sale',
        'request_date': new Date().toISOString(),
        'page_type': this.getPageType(),
        'page_url': window.location.href
      };

      console.log('Identify Data:', identifyData);

      // Push to Klaviyo
      console.log('Pushing identify to _learnq...');
      window._learnq.push(['identify', identifyData]);
      console.log('✓ Identify pushed');

      // Subscribe to list
      if (this.config.klaviyoListId) {
        console.log('Subscribing to list:', this.config.klaviyoListId);
        const subscribeData = {
          list: this.config.klaviyoListId,
          email: email
        };
        console.log('Subscribe Data:', subscribeData);
        window._learnq.push(['subscribe', subscribeData]);
        console.log('✓ Subscribe to list pushed');
      } else {
        console.warn('⚠️ No Klaviyo List ID configured');
      }

      // Track custom event
      const eventData = {
        'page_type': this.getPageType(),
        'page_url': window.location.href
      };

      console.log('Pushing track event to _learnq...');
      console.log('Event Data:', eventData);
      window._learnq.push(['track', 'Requested Skinbetter Sale Code', eventData]);
      console.log('✓ Track event pushed');

      console.log('_learnq queue length:', window._learnq.length);
      console.log('=== Klaviyo Submission Complete ===');

      return Promise.resolve({ success: true });
    } catch (error) {
      console.error('❌ Klaviyo submission error:', error);
      throw error;
    }
  }

  /**
   * Get current page type
   */
  getPageType() {
    if (document.querySelector('[data-section-type="product"]')) {
      return 'product';
    }
    if (document.querySelector('[data-section-type="collection"]')) {
      return 'collection';
    }
    return 'unknown';
  }

  /**
   * Set request form loading state
   */
  setRequestLoading(isLoading) {
    if (this.elements.requestSubmitBtn) {
      this.elements.requestSubmitBtn.disabled = isLoading;
    }

    if (this.elements.requestSubmitText) {
      this.elements.requestSubmitText.style.display = isLoading ? 'none' : 'inline';
    }

    if (this.elements.requestSpinner) {
      this.elements.requestSpinner.classList.toggle('hidden', !isLoading);
    }
  }

  /**
   * Show request error message
   */
  showRequestError(message) {
    if (this.elements.requestError && this.elements.requestErrorText) {
      this.elements.requestErrorText.textContent = message;
      this.elements.requestError.style.display = 'flex';
    }
  }

  /**
   * Hide request error message
   */
  hideRequestError() {
    if (this.elements.requestError) {
      this.elements.requestError.style.display = 'none';
    }
  }

  /**
   * Show the gate
   */
  showGate() {
    if (this.elements.overlay) {
      this.elements.overlay.style.display = 'flex';
      document.body.setAttribute('data-locked', 'true');
      document.body.style.overflow = 'hidden';
    }
  }

  /**
   * Hide the gate
   */
  hideGate() {
    if (this.elements.overlay) {
      this.elements.overlay.style.opacity = '0';
      setTimeout(() => {
        this.elements.overlay.style.display = 'none';
        document.body.removeAttribute('data-locked');
        document.body.style.overflow = '';
      }, 300);
    }
  }

  /**
   * Set cookie
   */
  setCookie(name, value, hours) {
    const date = new Date();
    date.setTime(date.getTime() + (hours * 60 * 60 * 1000));
    const expires = '; expires=' + date.toUTCString();
    document.cookie = name + '=' + value + expires + '; path=/';
  }

  /**
   * Get cookie
   */
  getCookie(name) {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');

    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') {
        c = c.substring(1, c.length);
      }
      if (c.indexOf(nameEQ) === 0) {
        return c.substring(nameEQ.length, c.length);
      }
    }

    return null;
  }

  /**
   * Handle copy code button click
   */
  handleCopyCode() {
    const code = this.config.password.toUpperCase();

    // Use Clipboard API
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(code).then(() => {
        this.showCopyFeedback();
      }).catch((err) => {
        console.error('Failed to copy:', err);
        // Fallback: just show feedback anyway
        this.showCopyFeedback();
      });
    } else {
      // Fallback for older browsers
      this.showCopyFeedback();
    }
  }

  /**
   * Show temporary "Copied!" feedback
   */
  showCopyFeedback() {
    if (!this.elements.codeDisplay) return;

    const originalText = this.elements.codeDisplay.textContent;
    this.elements.codeDisplay.textContent = 'COPIED!';

    setTimeout(() => {
      this.elements.codeDisplay.textContent = originalText;
    }, 2000);
  }
}

// Initialize on page load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new PasswordGate();
  });
} else {
  new PasswordGate();
}
